import type { RootState } from 'src/redux/store';
import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import { merge } from 'es-toolkit';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

import { _contacts } from 'src/_mock';
import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { usePremissionContext } from 'src/pages/auth/hooks';
import { ChatAssignedTo } from 'src/pages/chat/components/chat-assigned-to';
import { useModulePermissions } from 'src/pages/auth/hooks/useModulePermissions';

import { Logo } from 'src/components/logo';
import { useSettingsContext } from 'src/components/settings';

import { NavMobile } from './nav-mobile';
import { VerticalDivider } from './content';
import { NavVertical } from './nav-vertical';
import { DialerPopover } from './call-dialer';
import { InternalCall } from './internal-call';
import { layoutClasses } from '../core/classes';
import { NavHorizontal } from './nav-horizontal';
import { _account } from '../nav-config-account';
import { MainSection } from '../core/main-section';
import { MenuButton } from '../components/menu-button';
import { HeaderSection } from '../core/header-section';
import { LayoutSection } from '../core/layout-section';
import { AccountDrawer } from '../components/account-drawer';
import { LanguagePopover } from '../components/language-popover';
import { ToggleThemeSwitch } from '../components/dark-light-button';
import { WorkspacesPopover } from '../components/workspaces-popover';
import { navData as dashboardNavData } from '../nav-config-dashboard';
import { dashboardLayoutVars, dashboardNavColorVars } from './css-vars';
import { NotificationsDrawer } from '../components/notifications-drawer';

import type { MainSectionProps } from '../core/main-section';
import type { HeaderSectionProps } from '../core/header-section';
import type { LayoutSectionProps } from '../core/layout-section';

// ----------------------------------------------------------------------

type LayoutBaseProps = Pick<LayoutSectionProps, 'sx' | 'children' | 'cssVars'>;

export type DashboardLayoutProps = LayoutBaseProps & {
  layoutQuery?: Breakpoint;
  slotProps?: {
    header?: HeaderSectionProps;
    nav?: {
      data?: NavSectionProps['data'];
    };
    main?: MainSectionProps;
  };
};

export function DashboardLayout({
  sx,
  cssVars,
  children,
  slotProps,
  layoutQuery = 'lg',
}: DashboardLayoutProps) {
  const theme = useTheme();

  const settings = useSettingsContext();
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);
  const { t } = useTranslate('translation');
  const { handleNavPremissionCheck } = usePremissionContext();

  const navVars = dashboardNavColorVars(theme, settings.state.navColor, settings.state.navLayout);
  const { isView } = useModulePermissions('user');
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const navData = slotProps?.nav?.data ?? handleNavPremissionCheck(dashboardNavData);

  const isNavMini = settings.state.navLayout === 'mini';
  const isNavHorizontal = settings.state.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.state.navLayout === 'vertical';

  const renderHeader = () => {
    const headerSlotProps: HeaderSectionProps['slotProps'] = {
      container: {
        maxWidth: false,
        sx: {
          ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
          ...(isNavHorizontal && {
            bgcolor: 'var(--layout-nav-bg)',
            height: { [layoutQuery]: 'var(--layout-nav-horizontal-height)' },
            [`& .${iconButtonClasses.root}`]: { color: 'var(--layout-nav-text-secondary-color)' },
          }),
        },
      },
    };

    const headerSlots: HeaderSectionProps['slots'] = {
      topArea: (
        <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
          This is an info Alert.
        </Alert>
      ),
      bottomArea: isNavHorizontal ? (
        <NavHorizontal data={navData} layoutQuery={layoutQuery} cssVars={navVars.section} />
      ) : null,
      leftArea: (
        <>
          {/** @slot Nav mobile */}
          <MenuButton
            onClick={onOpen}
            sx={{ mr: 1, ml: -1, [theme.breakpoints.up(layoutQuery)]: { display: 'none' } }}
          />
          <NavMobile data={navData} open={open} onClose={onClose} cssVars={navVars.section} />

          {/** @slot Logo */}
          {isNavHorizontal && (
            <Logo
              sx={{
                display: 'none',
                [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
              }}
            />
          )}

          {/** @slot Divider */}
          {isNavHorizontal && (
            <VerticalDivider sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }} />
          )}

          {/** @slot Workspace popover */}
          <WorkspacesPopover
            data={[loginApiData]}
            sx={{ color: 'var(--layout-nav-text-primary-color)' }}
          />
        </>
      ),
      rightArea: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          {/** @slot Searchbar */}
          <ToggleThemeSwitch />

          {/* <Searchbar data={navData} /> */}
          {/** @slot assgined to button */}

          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && <DialerPopover />}

          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && <InternalCall />}

          {isView &&
            loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && (
              <ChatAssignedTo data={_contacts} />
            )}

          {/** @slot Language popover */}
          <LanguagePopover
            data={[
              { value: 'en', label: 'English', countryCode: 'GB' },
              // { value: 'fr', label: 'French', countryCode: 'FR' },
              // { value: 'vi', label: 'Vietnamese', countryCode: 'VN' },
              // { value: 'cn', label: 'Chinese', countryCode: 'CN' },
              { value: 'ar', label: 'Arabic', countryCode: 'SA' },
              // { value: 'he', label: 'Israel', countryCode: 'IL' },
            ]}
          />

          {/** @slot Notifications popover */}
          {loginApiData &&
            'tenant' in loginApiData &&
            typeof loginApiData.tenant === 'object' &&
            loginApiData.tenant !== null &&
            'name' in loginApiData.tenant &&
            (loginApiData.tenant as { name?: string }).name &&
            (loginApiData.tenant as { name?: string }).name !== 'MASTER' && <NotificationsDrawer />}

          {/** @slot Contacts popover */}

          {/** @slot Settings button */}
          {/* <SettingsButton /> */}

          {/** @slot buy now button */}

          {/** @slot Account drawer */}

          <AccountDrawer data={_account(t)} />
        </Box>
      ),
    };

    return (
      <HeaderSection
        layoutQuery={layoutQuery}
        disableElevation={isNavVertical}
        {...slotProps?.header}
        slots={{ ...headerSlots, ...slotProps?.header?.slots }}
        slotProps={merge(headerSlotProps, slotProps?.header?.slotProps ?? {})}
        sx={slotProps?.header?.sx}
      />
    );
  };

  const renderSidebar = () => (
    <NavVertical
      data={navData}
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      cssVars={navVars.section}
      onToggleNav={() =>
        settings.setField(
          'navLayout',
          settings.state.navLayout === 'vertical' ? 'mini' : 'vertical'
        )
      }
    />
  );

  const renderFooter = () => null;

  const renderMain = () => <MainSection {...slotProps?.main}>{children}</MainSection>;

  return (
    <LayoutSection
      /** **************************************
       * @Header
       *************************************** */
      headerSection={renderHeader()}
      /** **************************************
       * @Sidebar
       *************************************** */
      sidebarSection={isNavHorizontal ? null : renderSidebar()}
      /** **************************************
       * @Footer
       *************************************** */
      footerSection={renderFooter()}
      /** **************************************
       * @Styles
       *************************************** */
      cssVars={{ ...dashboardLayoutVars(theme), ...navVars.layout, ...cssVars }}
      sx={[
        {
          [`& .${layoutClasses.sidebarContainer}`]: {
            [theme.breakpoints.up(layoutQuery)]: {
              pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
              transition: theme.transitions.create(['padding-left'], {
                easing: 'var(--layout-transition-easing)',
                duration: 'var(--layout-transition-duration)',
              }),
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {renderMain()}
    </LayoutSection>
  );
}
